import { useMemo } from 'react'
import { useAgencyDocument, type AgencyDocumentViewerState } from '../hooks/useAgencyDocument'
import { AgencyOriginalFormat, getAgencyOriginalFormat } from '../utils/agency-original-format'
import { plainTextHighlightPassages } from '../utils/agency-text-original'
import { getAgencyPublisherUrl } from '../utils/get-agency-publisher-url'
import RenderReactPdfViewer from '@/components/react-pdf-viewer/RenderReactPdfViewer'
import HTMLBaseRenderer from '@/content-viewer/components/base-renderers/HTMLBaseRenderer'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import ContentViewerLoadingSpinner from '@/content-viewer/components/common/ContentViewerLoadingStates'
import { Button } from '@/components/button/Button'

export default function AgencyDocumentViewer({ conversationId, referenceId, tableId }: { conversationId?: string; referenceId: string; tableId?: string }) {
  const state = useAgencyDocument(conversationId, referenceId, tableId)
  return <AgencyDocumentViewerContent state={state} />
}

function PdfLoadingFrame() {
  return (
    <div
      className="mx-auto flex aspect-[592/725] w-full max-w-[592px] items-center justify-center rounded border border-brand-neutral-200 bg-white shadow-xs"
      aria-label="PDF loading"
    >
      <ContentViewerLoadingSpinner />
    </div>
  )
}

export function AgencyDocumentViewerContent({
  state,
  pdfDefaultScale,
  showOfficialSource = true,
}: {
  state: AgencyDocumentViewerState
  pdfDefaultScale?: number
  /** Off on pages whose header already links the official source, so the link appears once. */
  showOfficialSource?: boolean
}) {
  const { data, file, pdfHighlights, html, error, notFound, originalError, retry } = state
  const format = data?.original_available ? getAgencyOriginalFormat(data.media_type) : null
  const textHighlights = useMemo(() => {
    const passages = data?.selected_spans.map((span) => span.text) ?? []
    return format === AgencyOriginalFormat.PLAIN_TEXT ? plainTextHighlightPassages(passages) : passages
  }, [data, format])
  // Reloading cannot bring back a document that is no longer indexed, so it gets no Retry.
  if (notFound) return <ContentViewerErrorState message="This document is no longer available." />
  if (error) return <ContentViewerErrorState message="Could not load this decision." onRetry={retry} />
  if (!data)
    return (
      <div className="h-full overflow-auto bg-brand-neutral-50 p-3">
        <PdfLoadingFrame />
      </div>
    )
  if (format === AgencyOriginalFormat.PDF) {
    if (originalError) return <ContentViewerErrorState message="Could not load this PDF." onRetry={retry} />
    return (
      <div className="h-full min-h-0 overflow-auto bg-brand-neutral-50 p-3">
        {file ? <RenderReactPdfViewer pdfFile={file} highlightAreas={pdfHighlights} defaultScale={pdfDefaultScale} /> : <PdfLoadingFrame />}
      </div>
    )
  }
  if (format === AgencyOriginalFormat.PLAIN_TEXT || format === AgencyOriginalFormat.MARKDOWN) {
    if (originalError) return <ContentViewerErrorState message="Could not load this document." onRetry={retry} />
    if (!html) return <ContentViewerLoadingSpinner />
    return (
      <div className="h-full min-h-0 overflow-auto bg-brand-neutral-50">
        <HTMLBaseRenderer htmlContent={html} highlightSentences={textHighlights} />
      </div>
    )
  }
  const publisherUrl = getAgencyPublisherUrl(data.source_url, data.publisher_media_type)
  // A document opened by its own ID, rather than from a citation, has no saved evidence.
  const hasSavedEvidence = data.selected_spans.length > 0
  return (
    <div className="flex h-full min-h-0 flex-col gap-3 overflow-auto p-3">
      <div>
        <p>{hasSavedEvidence ? 'Original document is unavailable. The saved evidence is below.' : 'Original document is unavailable.'}</p>
        <Button variant="plain" color="primary" size="sm" onClick={retry}>
          Retry
        </Button>
      </div>
      {showOfficialSource && publisherUrl && (
        <a href={publisherUrl.toString()} target="_blank" rel="noopener noreferrer">
          Official source
        </a>
      )}
      {hasSavedEvidence && (
        <section>
          <h2 className="text-sm font-semibold">Saved evidence</h2>
          {data.selected_spans.map((span, index) => (
            <blockquote key={index} className="my-2 whitespace-pre-wrap">
              {span.text}
            </blockquote>
          ))}
        </section>
      )}
    </div>
  )
}
